import * as Y from "yjs";
import {create} from "zustand";
import {
  NodeSide,
  VisionBoardColor,
  VisionBoardEdge,
  VisionBoardEdgeId,
  VisionBoardNodeId,
} from "../../cdx-models/utils/extended-fields";
import {
  EdgeBushEntry,
  NodeAndSideAnchorPos,
  NodeBushEntry,
  NodeMoveArg,
  TreeStore,
} from "./useVisionBoardData";
import {
  cameraToWorld,
  evaluateBezier,
  getAnchorPos,
  getDistSquared,
  getNearestSide,
  TwoNums,
  VisionBoardBag,
} from "./canvas-utils";
import {
  forwardRef,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {Box, css, cx, DSInput} from "@cdx/ds";
import {setupDragHandlers} from "../../lib/hooks/use-drag";
import {CanvasEdgeMenu} from "./CanvasMenu";
import {useEsc} from "@cdx/common";
import {dsColors} from "@cdx/ds/css/tokens";
import {msNextThemes} from "../../pages/milestones/Timeline/Timeline.css";
import {canvasEdgeStyles} from "./vision-board.css";
import {createTextNode} from "./canvas-node-utils";

type NodeAndSide = {node: NodeBushEntry; side: NodeSide};

type TempArrow =
  | {
      type: "creation";
      source: NodeAndSide;
      relativeTarget: TwoNums;
    }
  | {
      type: "changeTarget";
      edgeId: VisionBoardEdgeId;
      source: NodeAndSideAnchorPos;
      target: TwoNums;
    }
  | {
      type: "changeSource";
      edgeId: VisionBoardEdgeId;
      source: TwoNums;
      target: NodeAndSideAnchorPos;
    };

export const useCanvasTempEdgeStore = create<{arrow: TempArrow | null}>(() => ({
  arrow: null,
}));

const getTargetNodeAndSide = (pos: TwoNums, sourceNodeId: VisionBoardNodeId, store: TreeStore) => {
  const [x, y] = pos;
  const targets = store.getNodesSnapshot({minX: x - 5, maxX: x + 5, minY: y - 5, maxY: y + 5});
  if (!targets.length) return null;
  const firstValidTarget = targets.findLast((t) => t.id !== sourceNodeId);
  if (!firstValidTarget) return null;
  const side = getNearestSide(firstValidTarget, pos);
  return {node: firstValidTarget, side};
};

const getTargetlessEndSide = (
  sourceSide: NodeSide,
  endPos: TwoNums,
  [sx, sy]: TwoNums
): NodeSide => {
  switch (sourceSide) {
    case "left":
    case "right":
      return endPos[0] > sx ? "left" : "right";
    case "top":
    case "bottom":
      return endPos[1] > sy ? "top" : "bottom";
  }
};

const getStartAndEnd = (
  arrow: TempArrow,
  bag: VisionBoardBag
): {
  start: [TwoNums, NodeSide, VisionBoardNodeId | null];
  end: [TwoNums, NodeSide, VisionBoardNodeId | null];
} => {
  const {posRef, store} = bag;
  const scale = posRef.current.scale;
  const getEnd = (
    source: NodeAndSideAnchorPos,
    pos: TwoNums
  ): [TwoNums, NodeSide, VisionBoardNodeId | null] => {
    const target = getTargetNodeAndSide(pos, source.node.id, store);
    if (!target) {
      return [pos, getTargetlessEndSide(source.side, pos, source.anchor), null];
    } else {
      return [getAnchorPos(target), target.side, target.node.id];
    }
  };
  const getStart = (
    target: NodeAndSideAnchorPos,
    pos: TwoNums
  ): [TwoNums, NodeSide, VisionBoardNodeId | null] => {
    const source = getTargetNodeAndSide(pos, target.node.id, store);
    if (!source) {
      return [pos, getTargetlessEndSide(target.side, pos, target.anchor), null];
    } else {
      return [getAnchorPos(source), source.side, source.node.id];
    }
  };
  switch (arrow.type) {
    case "creation": {
      const {source, relativeTarget} = arrow;
      const [x, y] = relativeTarget;
      const sourcePos = getAnchorPos(arrow.source);
      const mousePos = [x / scale + sourcePos[0], y / scale + sourcePos[1]] as TwoNums;
      return {
        start: [sourcePos, source.side, source.node.id],
        end: getEnd({node: source.node, side: source.side, anchor: sourcePos}, mousePos),
      };
    }
    case "changeTarget": {
      const {source, target} = arrow;
      return {
        start: [source.anchor, source.side, source.node.id],
        end: getEnd(source, cameraToWorld(target, posRef)),
      };
    }
    case "changeSource": {
      const {source, target} = arrow;
      return {
        start: getStart(target, cameraToWorld(source, posRef)),
        end: [target.anchor, target.side, target.node.id],
      };
    }
    default:
      throw new Error();
  }
};

export const onEdgeDrop = (arrow: TempArrow, bag: VisionBoardBag): VisionBoardEdge["value"] => {
  const {
    start: [[sx, sy], sourceSide, sourceId],
    end: [[ex, ey], endSide, endId],
  } = getStartAndEnd(arrow, bag);
  if (sourceId && endId) {
    return {
      source: {nodeId: sourceId, side: sourceSide},
      target: {nodeId: endId, side: endSide},
    };
  } else if (sourceId) {
    const {id} = createTextNode(bag, {anchor: {side: endSide, pos: [ex, ey]}});
    bag.selectionStore.setNodeIds([id]);
    return {
      source: {nodeId: sourceId, side: sourceSide},
      target: {nodeId: id, side: endSide},
    };
  } else if (endId) {
    const {id} = createTextNode(bag, {anchor: {side: sourceSide, pos: [sx, sy]}});
    bag.selectionStore.setNodeIds([id]);
    return {
      source: {nodeId: id, side: sourceSide},
      target: {nodeId: endId, side: endSide},
    };
  } else {
    throw new Error("Can't  create edge without source or target node");
  }
};

const PresentTempEdge = (props: {bag: VisionBoardBag}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const bagRef = useRef(props.bag);
  bagRef.current = props.bag;
  useEffect(() => {
    return useCanvasTempEdgeStore.subscribe((s) => {
      if (!s.arrow) return;
      const {
        start: [[sx, sy], sourceSide],
        end: [[ex, ey], endSide],
      } = getStartAndEnd(s.arrow, bagRef.current);

      const [minX, maxX] = ex < sx ? [ex, sx] : [sx, ex];
      const [minY, maxY] = ey < sy ? [ey, sy] : [sy, ey];

      const svgNode = svgRef.current!;

      svgNode.style.left = `${minX - SVG_PADDING}px`;
      svgNode.style.top = `${minY - SVG_PADDING}px`;
      svgNode.setAttribute("width", `${maxX - minX + 2 * SVG_PADDING}`);
      svgNode.setAttribute("height", `${maxY - minY + 2 * SVG_PADDING}`);

      const sPos: TwoNums = [sx - minX + SVG_PADDING, sy - minY + SVG_PADDING];
      const ePos: TwoNums = [ex - minX + SVG_PADDING, ey - minY + SVG_PADDING];
      const pathPoints = getControlPoints(sPos, sourceSide, ePos, endSide);
      pathRef.current!.setAttribute("d", getSvgPath(pathPoints));
    });
  }, []);
  return (
    <svg
      className={css({position: "absolute", pointerEvents: "none"})}
      width="0"
      height="0"
      ref={svgRef}
    >
      <path
        stroke={dsColors.gray300}
        strokeWidth={2}
        fill="none"
        markerEnd={`url(#${ArrowHeadId})`}
        ref={pathRef}
      />
    </svg>
  );
};

export const RenderTempEdge = ({bag}: {bag: VisionBoardBag}) => {
  const arrowSource = useCanvasTempEdgeStore((s) => s.arrow ?? null);
  if (!arrowSource) return null;
  return <PresentTempEdge bag={bag} />;
};

const SVG_PADDING = 100;
type FourPoints = [TwoNums, TwoNums, TwoNums, TwoNums];

const getControlPoints = (
  start: TwoNums,
  sSide: NodeSide,
  rawEnd: TwoNums,
  tSide: NodeSide
): FourPoints => {
  const getEnd = (): TwoNums => {
    switch (tSide) {
      case "left":
        return [rawEnd[0] - 3, rawEnd[1]];
      case "right":
        return [rawEnd[0] + 3, rawEnd[1]];
      case "top":
        return [rawEnd[0], rawEnd[1] - 3];
      case "bottom":
        return [rawEnd[0], rawEnd[1] + 3];
    }
  };
  const end = getEnd();
  const getControlPoint = (side: NodeSide, [x, y]: TwoNums): TwoNums => {
    switch (side) {
      case "left":
        return [x - 100, y];
      case "right":
        return [x + 100, y];
      case "top":
        return [x, y - 100];
      case "bottom":
        return [x, y + 100];
    }
  };
  return [start, getControlPoint(sSide, start), getControlPoint(tSide, end), end];
};

const getSvgPath = ([p1, p2, p3, p4]: FourPoints) =>
  `M ${p1[0]} ${p1[1]} C ${p2[0]} ${p2[1]}, ${p3[0]} ${p3[1]}, ${p4[0]} ${p4[1]}`;

const relativeEdgePos = ([x, y]: TwoNums, edge: {minX: number; minY: number}): TwoNums => [
  x - edge.minX + SVG_PADDING,
  y - edge.minY + SVG_PADDING,
];

const useEdgePos = (edge: EdgeBushEntry, bag: VisionBoardBag) => {
  const sub = useMemo(() => {
    return (cb: () => void) => bag.store.registerEdgeListener(edge.id, cb);
  }, [edge.id, bag.store]);
  const lastRes = useRef<[TwoNums, TwoNums] | null>(null);
  return useSyncExternalStore(sub, () => {
    const start = relativeEdgePos(edge.source.anchor, edge);
    const end = relativeEdgePos(edge.target.anchor, edge);
    const lr = lastRes.current;
    if (
      lr &&
      lr[0][0] === start[0] &&
      lr[0][1] === start[1] &&
      lr[1][0] === end[0] &&
      lr[1][1] === end[1]
    ) {
      return lr;
    }
    lastRes.current = [start, end];
    return lastRes.current;
  });
};

const ArrowHeadId = "cdx-arrowhead";

const cols = ["green", "blue", "pink", "red", "yellow", "brown"] as const;
const arrowSize = 7;
const markerProps = {
  markerWidth: arrowSize,
  markerHeight: arrowSize,
  refX: arrowSize - 1,
  refY: arrowSize / 2,
  orient: "auto",
};
const markerPath = `M0,0 L${arrowSize},${arrowSize / 2} L0,${arrowSize} L2,${arrowSize / 2} Z`;

export const ArrowDef = () => (
  <svg>
    <defs>
      <marker id={ArrowHeadId} {...markerProps}>
        <path d={markerPath} fill={dsColors.gray300} />
      </marker>
      {cols.map((col) => (
        <marker id={`${ArrowHeadId}-${col}`} key={col} {...markerProps}>
          <path
            d={markerPath}
            className={cx(msNextThemes[col], canvasEdgeStyles.marker.withCustomColor)}
          />
        </marker>
      ))}
      <marker id={`${ArrowHeadId}-active`} {...markerProps}>
        <path d={markerPath} className={canvasEdgeStyles.marker.active} />
      </marker>
    </defs>
  </svg>
);

const createEdgeDataObserver = <T,>(edgeMap: Y.Map<any>, extractVal: (map: Y.Map<any>) => T) => {
  return {
    subscribe: (cb: () => void) => {
      edgeMap.observe(cb);
      return () => edgeMap.unobserve(cb);
    },
    getSnapshot: () => extractVal(edgeMap),
  };
};

const LabelEdit = (props: {label: string; onSave: (next: string) => void; bag: VisionBoardBag}) => {
  const {label, bag} = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const propsRef = useRef(props);
  propsRef.current = props;
  const handleBlur = () => {
    bag.selectionStore.clear();
  };
  useEffect(() => {
    const node = inputRef.current!;
    return () => {
      const nextLabel = node.value;
      if (nextLabel !== propsRef.current.label) propsRef.current.onSave(nextLabel);
    };
  }, []);
  useEsc(() => {
    bag.selectionStore.clear();
  });
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      bag.selectionStore.clear();
    }
  };
  return (
    <DSInput
      size="sm"
      onKeyDown={handleKeyDown}
      defaultValue={label}
      autoFocus
      onBlur={handleBlur}
      ref={inputRef}
      className={css({textAlign: "center"})}
      onMouseDown={(e) => e.stopPropagation()}
    />
  );
};

const useEdgeLabel = (edge: EdgeBushEntry) => {
  const [observer] = useState(() =>
    createEdgeDataObserver(edge.edge, (val) => val.get("label") ?? "")
  );
  return useSyncExternalStore(observer.subscribe, observer.getSnapshot);
};
export const useEdgeColor = (edge: EdgeBushEntry) => {
  const [observer] = useState(() =>
    createEdgeDataObserver(
      edge.edge,
      (val) => (val.get("color") ?? null) as VisionBoardColor | null
    )
  );
  return useSyncExternalStore(observer.subscribe, observer.getSnapshot);
};

type LabelProps = {
  points: FourPoints;
  bag: VisionBoardBag;
  edge: EdgeBushEntry;
  mode: null | "select" | "edit";
  color: VisionBoardColor | null;
};
const Label = forwardRef<HTMLDivElement, LabelProps>(({points, edge, mode, color, bag}, ref) => {
  const label = useEdgeLabel(edge);
  if (!label && mode !== "edit") return null;
  const [x, y] = evaluateBezier(points, 0.5);

  return (
    <Box
      absolute
      top="0"
      left="0"
      pointerEvents="auto"
      style={{left: x, top: y}}
      translateX="-50%"
      translateY="-50%"
      color="primary"
      textType="label14"
      ref={ref}
      px="4px"
      py="2px"
      bg="foreground"
      textAlign="center"
      className={cx(color && canvasEdgeStyles.label.withCustomColor)}
    >
      {mode === "edit" ? (
        <LabelEdit
          label={label}
          bag={bag}
          onSave={(nextLabel) => edge.edge.set("label", nextLabel)}
        />
      ) : (
        <div
          className={css({
            textType: "label14",
            color: "primary",
            rounded: 4,
            cursor: "text",
          })}
          onClick={() => bag.selectionStore.setEdgeId(edge.id, "edit")}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        >
          {label}
        </div>
      )}
    </Box>
  );
});

export const CanvasEdge = ({edge, bag}: {edge: EdgeBushEntry; bag: VisionBoardBag}) => {
  const [start, end] = useEdgePos(edge, bag);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const hoverPathRef = useRef<SVGPathElement>(null);
  const labelRef = useRef<HTMLDivElement | null>(null);
  const edgeRef = useRef<EdgeBushEntry>(edge);
  edgeRef.current = edge;
  const sourceNodeId = edge.source.node.id;
  const targetNodeId = edge.target.node.id;

  useEffect(() => {
    return bag.store.registerNodeMoveListener([sourceNodeId, targetNodeId], (changedMap) => {
      const currEdge = edgeRef.current;
      const getPos = ({anchor, side}: NodeAndSideAnchorPos, arg?: NodeMoveArg): TwoNums => {
        if (!arg) return anchor;
        switch (arg.type) {
          case "move":
            return [anchor[0] + arg.value[0], anchor[1] + arg.value[1]];
          case "rect":
            return getAnchorPos({node: arg.value, side});
        }
      };
      const startPos = getPos(currEdge.source, changedMap.get(sourceNodeId));
      const endPos = getPos(currEdge.target, changedMap.get(targetNodeId));

      const [sx, sy] = startPos;
      const [ex, ey] = endPos;

      const [minX, maxX] = ex < sx ? [ex, sx] : [sx, ex];
      const [minY, maxY] = ey < sy ? [ey, sy] : [sy, ey];

      const tempEdge = {minX, minY};

      const pathPoints = getControlPoints(
        relativeEdgePos(startPos, tempEdge),
        currEdge.source.side,
        relativeEdgePos(endPos, tempEdge),
        currEdge.target.side
      );

      pathRef.current!.setAttribute("d", getSvgPath(pathPoints));
      hoverPathRef.current!.setAttribute("d", getSvgPath(pathPoints));

      if (labelRef.current) {
        const [x, y] = evaluateBezier(pathPoints, 0.5);
        labelRef.current.style.left = `${x}px`;
        labelRef.current.style.top = `${y}px`;
      }

      const containerStyle = containerRef.current!.style;
      const svgNode = svgRef.current!;

      containerStyle.left = `${minX - SVG_PADDING}px`;
      containerStyle.top = `${minY - SVG_PADDING}px`;
      svgNode.setAttribute("width", `${maxX - minX + 2 * SVG_PADDING}`);
      svgNode.setAttribute("height", `${maxY - minY + 2 * SVG_PADDING}`);
    });
  }, [sourceNodeId, targetNodeId, bag.store]);

  const mode = bag.selectionStore.useSelectedEdge((selEdge) =>
    selEdge?.id === edge.id ? selEdge.mode : null
  );

  const handleClick = () => {
    if (mode === "select") {
      bag.selectionStore.setEdgeId(edge.id, "edit");
    } else {
      bag.selectionStore.setEdgeId(edge.id, "select");
    }
  };

  const [{handlers, unsubs}] = useState(() =>
    setupDragHandlers(
      (startPos, {anchor}: {anchor: "source" | "target"}) => {
        const liveEdge = edgeRef.current;
        const getArrow = (pos: TwoNums): TempArrow =>
          anchor === "target"
            ? {
                type: "changeTarget",
                edgeId: liveEdge.id,
                source: liveEdge.source,
                target: pos,
              }
            : {
                type: "changeSource",
                edgeId: liveEdge.id,
                source: pos,
                target: liveEdge.target,
              };

        useCanvasTempEdgeStore.setState({arrow: getArrow(startPos)});
        let lastPos = startPos;

        return {
          onMove: (pos) => {
            lastPos = pos;
            useCanvasTempEdgeStore.setState({arrow: getArrow(pos)});
          },
          onStop: (isCancelled) => {
            useCanvasTempEdgeStore.setState({arrow: null});
            if (!isCancelled) {
              const edgeParams = onEdgeDrop(getArrow(lastPos), bag);
              liveEdge.edge.set("value", edgeParams);
            }
          },
        };
      },
      (e) => {
        const native = e.nativeEvent;
        const getPos = (): TwoNums => {
          if (native instanceof MouseEvent) {
            return [native.clientX, native.clientY];
          } else if (native instanceof TouchEvent) {
            const t = native.touches[0];
            return [t.clientX, t.clientY];
          } else {
            throw new Error("Unknown event");
          }
        };

        const liveEdge = edgeRef.current;

        const getAnchor = (): {anchor: "source" | "target"; relPos: TwoNums} => {
          const pos = cameraToWorld(getPos(), bag.posRef);
          const sourceDist = getDistSquared(pos, liveEdge.source.anchor);
          const targetDist = getDistSquared(pos, liveEdge.target.anchor);
          return {anchor: sourceDist < targetDist ? "source" : "target", relPos: getPos()};
        };
        return {
          onCancel: () => {},
          onDone: getAnchor,
        };
      }
    )
  );
  useEffect(() => {
    return () => {
      unsubs.forEach((fn) => fn());
    };
  }, [unsubs]);

  const points = getControlPoints(start, edge.source.side, end, edge.target.side);
  const path = getSvgPath(points);

  const color = useEdgeColor(edge);

  const hide = useCanvasTempEdgeStore((s) =>
    Boolean(s.arrow && s.arrow.type !== "creation" && s.arrow.edgeId === edge.id)
  );

  return (
    <Box
      absolute
      style={{left: edge.minX - SVG_PADDING, top: edge.minY - SVG_PADDING}}
      ref={containerRef}
      pointerEvents="none"
      className={cx(color && msNextThemes[color])}
      zIndex={mode ? 1 : undefined}
      opacity={hide ? 0 : undefined}
    >
      <svg
        width={Math.ceil(edge.maxX - edge.minX + SVG_PADDING * 2)}
        height={Math.ceil(edge.maxY - edge.minY + SVG_PADDING * 2)}
        ref={svgRef}
        className={css({display: "block"})}
      >
        <path
          d={path}
          strokeWidth={12}
          stroke="transparent"
          fill="none"
          className={css({pointerEvents: "auto", cursor: "pointer"})}
          ref={hoverPathRef}
          {...handlers}
          onClick={handleClick}
        />
        <path
          d={path}
          className={cx(
            color ? canvasEdgeStyles.path.withCustomColor : canvasEdgeStyles.path.noCustomColor,
            mode && canvasEdgeStyles.path.active
          )}
          strokeWidth={2}
          fill="none"
          markerEnd={`url(#${ArrowHeadId}${mode ? "-active" : color ? `-${color}` : ""}`}
          ref={pathRef}
        />
      </svg>
      <Label points={points} bag={bag} edge={edge} ref={labelRef} mode={mode} color={color} />
      {mode === "select" && <CanvasEdgeMenu edge={edge} bag={bag} />}
    </Box>
  );
};
